<template>
    <div>
        <template v-if="loading">
            <loading-spinner/>
        </template>

        <template v-if="!loading">
            <risk-header :risk="riskHeader"/>
            <x-form ref="subscriptionAgreementForm" @submit.prevent="submit">
                <template class="thin-container">
                    <x-container grid-list-lg class="thin-container">
                        <x-layout>
                            <x-flex xs12>
                                <h3>Fiscal & Regulatory</h3>
                            </x-flex>
                        </x-layout>
                        <x-layout row>
                            <x-flex xs12>
                                <x-text-field
                                    v-model="subscriptionAgreement.insurers_tax_payable"
                                    label="Tax payable by insurers"
                                    placeholder=" "
                                    outline
                                    :rules="[
                                        v => ruleMaxCharacters(v, 300, 'Tax payable by insurers')
                                    ]"
                                />
                            </x-flex>
                        </x-layout>
                        <x-layout>
                            <x-flex xs12>
                                <x-autocomplete
                                    v-model="subscriptionAgreement.country_id"
                                    :items="countries"
                                    label="Country of origin"
                                    placeholder="Country"
                                    item-value="id"
                                    item-text="name"
                                    append-icon="keyboard_arrow_down"
                                    outline
                                />
                            </x-flex>
                        </x-layout>

                        <x-layout>
                            <x-flex xs12>
                                <x-text-field
                                    v-model="subscriptionAgreement.risk_locations"
                                    label="Regulatory risk locations"
                                    placeholder=" "
                                    outline
                                    :rules="[
                                        v => ruleMaxCharacters(v, 300, 'Regulatory risk locations')
                                    ]"
                                />
                            </x-flex>
                        </x-layout>

                        <x-layout>
                            <x-flex xs12>
                                <x-text-field
                                    v-model="subscriptionAgreement.overseas_broker"
                                    label="Overseas broker"
                                    placeholder=" "
                                    outline
                                    :rules="[
                                        v => ruleMaxCharacters(v, 300, 'Overseas broker')
                                    ]"
                                />
                            </x-flex>
                        </x-layout>

                        <x-layout>
                            <x-flex xs12>
                                <x-text-field
                                    v-model="subscriptionAgreement.surplus_line_broker"
                                    label="Surplus lines broker"
                                    placeholder=" "
                                    outline
                                    :rules="[
                                        v => ruleMaxCharacters(v, 300, 'Surplus lines broker')
                                    ]"
                                />
                            </x-flex>
                        </x-layout>

                        <x-layout>
                            <x-flex xs12>
                                <x-text-field
                                    v-model="subscriptionAgreement.state_of_filling"
                                    label="State of filing"
                                    placeholder=" "
                                    outline
                                    :rules="[
                                        v => ruleMaxCharacters(v, 300, 'State of filing')
                                    ]"
                                />
                            </x-flex>
                        </x-layout>

                        <x-layout>
                            <x-flex xs12>
                                <x-text-field
                                    v-model="subscriptionAgreement.us_classification"
                                    label="US classification"
                                    placeholder=" "
                                    outline
                                    :rules="[
                                        v => ruleMaxCharacters(v, 300, 'US classification')
                                    ]"
                                />
                            </x-flex>
                        </x-layout>

                        <x-layout>
                            <x-flex xs12>
                                <x-text-field
                                    v-model="subscriptionAgreement.coding_premium_allocation"
                                    label="Allocation of premium to coding"
                                    placeholder=" "
                                    outline
                                    :rules="[
                                        v => ruleMaxCharacters(v, 300, 'Allocation of premium to coding')
                                    ]"
                                />
                            </x-flex>
                        </x-layout>

                        <x-layout>
                            <x-flex xs12>
                                <x-text-field
                                    v-model="subscriptionAgreement.client_classification"
                                    label="Regulatory client classification"
                                    placeholder=" "
                                    outline
                                    :rules="[
                                        v => ruleMaxCharacters(v, 300, 'Regulatory client classification')
                                    ]"
                                />
                            </x-flex>
                        </x-layout>

                        <x-layout>
                            <x-flex xs12>
                                <h3>Broker Remuneration & Deductions</h3>
                            </x-flex>
                        </x-layout>

                        <x-layout mt-3>
                            <x-flex xs12>
                                <x-text-field
                                    v-model="subscriptionAgreement.client_payable_fee"
                                    label="Fee payable by client"
                                    placeholder=" "
                                    outline
                                    :rules="[
                                        v => ruleMaxCharacters(v, 300, 'Fee payable by client')
                                    ]"
                                />
                            </x-flex>
                        </x-layout>

                        <x-layout>
                            <x-flex xs12>
                                <x-text-field
                                    v-model="subscriptionAgreement.total_brokerage"
                                    label="Total brokerage"
                                    placeholder=" "
                                    outline
                                    :rules="[
                                        v => ruleMaxCharacters(v, 300, 'Total brokerage')
                                    ]"
                                />
                            </x-flex>
                        </x-layout>

                        <x-layout>
                            <x-flex xs12>
                                <x-text-field
                                    v-model="subscriptionAgreement.other_premium_deductions"
                                    label="Other deductions from premium"
                                    placeholder=" "
                                    outline
                                    :rules="[
                                        v => ruleMaxCharacters(v, 300, 'Other deductions from premium')
                                    ]"
                                />
                            </x-flex>
                        </x-layout>

                        <x-layout row>
                            <x-flex xs12>
                                <x-menu
                                    v-model="settlementDueDateInput"
                                    :close-on-content-click="false"
                                    :nudge-right="40"
                                    lazy
                                    transition="scale-transition"
                                    offset-y
                                    full-width
                                    max-width="300"
                                >
                                    <template v-slot:activator="{ on }">
                                        <x-text-field
                                            v-model="subscriptionAgreement.settlement_due_date"
                                            label="Settlement due date"
                                            placeholder=" "
                                            append-icon="event"
                                            outline
                                            readonly
                                            v-on="on"
                                        />
                                    </template>
                                    <x-date-picker
                                        v-model="subscriptionAgreement.settlement_due_date"
                                        @input="settlementDueDateInput = false"
                                    />
                                </x-menu>
                            </x-flex>
                        </x-layout>

                        <x-layout layout justify-space-between>
                                <x-flex xs6>
                                    <x-btn color="tertiary" @click="goToDeclaration()">Cancel</x-btn>
                                </x-flex>
                                <x-flex xs6 text-xs-right>
                                    <x-btn
                                        class="ma-0"
                                        color="primary"
                                        :loading="formLoading"
                                        @click="submitForm"
                                    >
                                        {{ isUpdating ? 'Update' : 'Save'}}
                                    </x-btn>
                                </x-flex>
                        </x-layout>
                    </x-container>
                </template>
            </x-form>

            <modal
                ref="confirmSubscriptionAgreement"
                title="Confirm subscription agreement"
                confirmation
            >
                <div class="text-xs-center">
                    <p>
                        <span v-if="isUpdating">You are about to update an existing subscription agreement.</span>
                        <span v-else>You are about to submit a subscription agreement.</span>
                    </p>

                    <p>
                        Are you sure?
                    </p>
                </div>
            </modal>
        </template>
    </div>
</template>

<script>
    import RiskHeader from '~/components/Shared/Partials/RiskHeader';
    import Modal from "~/components/Shared/UIComponents/Modal";
    import rules from '~/mixins/rules';

    export default {
        name: 'BrokerSubscriptionAgreementEntry',
        mixins: [rules],
        components: {
            RiskHeader,
            Modal
        },
        data() {
            return {
                loading: true,
                declaration: {currency: {}},
                subscriptionAgreement: {
                    insurers_tax_payable: null,
                    country_id: null,
                    risk_locations: null,
                    overseas_broker: null,
                    surplus_line_broker: null,
                    state_of_filling: null,
                    us_classification: null,
                    coding_premium_allocation: null,
                    client_classification: null,
                    client_payable_fee: null,
                    total_brokerage: null,
                    other_premium_deductions: null,
                    settlement_due_date: null,
                },
                isUpdating: false,
                countries: [],
                settlementDueDateInput: false,
                formLoading: false
            }
        },
        computed: {
            riskHeader() {
                return {
                    id: this.declaration.risk_id,
                    client_company_name: this.declaration.client_company.name,
                    obligor_name: this.declaration.obligor_name,
                    country_name: this.declaration.country_id ? this.declaration.country.name : '',
                    status: this.declaration.status
                };
            },
        },
        created() {
            this.fetchData();
        },
        methods: {
            async fetchData() {
                const dataRequests = [];

                dataRequests.push(axios.get(this.$root.api(`broker/declaration/${this.$route.params.id}`)).then(res => {
                    this.declaration = res.data.data;
                }));

                this.countries = this.$root.countries

                axios.all(dataRequests).then(() => {
                    if (this.$route.params.subscriptionAgreementId) {
                        const subscriptionAgreementId = Number(this.$route.params.subscriptionAgreementId);
                        this.subscriptionAgreement = this.declaration.subscription_agreements.find(subAgreement => subAgreement.id === subscriptionAgreementId);
                        this.isUpdating = true;
                    }
                    this.loading = false;
                });
            },
            async submitForm() {
                if (this.$refs.subscriptionAgreementForm.validate()) {
                    this.formLoading = true;

                    if (await this.$refs.confirmSubscriptionAgreement.open() === false) {
                        this.formLoading = false;
                        return;
                    }

                    await this.submit()
                        .then(response => {
                            this.formLoading = false;
                            this.goToDeclaration();
                        }).catch((error) => {
                            this.formLoading = false;
                            this.loginFailure(error);
                        });
                }
            },
            submit() {
                if (this.isUpdating) {
                    return axios.put(this.$root.api(`broker/declaration/${this.declaration.id}/subscription-agreements/${this.subscriptionAgreement.id}`), this.subscriptionAgreement)
                }

                return axios.post(this.$root.api(`broker/declaration/${this.declaration.id}/subscription-agreements`), this.subscriptionAgreement)
            },
            goToDeclaration() {
                this.$router.push({path: `/agent/risk/${this.$route.params.id}`});
            },
        }
    }
</script>

<style lang="scss" scoped>
    .x-form {
        > .container.thin-container {
            width: 100%;
            max-width: 585px;
        }
    }
</style>
